// theme.ts (MUI v5+)
import { createTheme, alpha, darken, lighten } from "@mui/material/styles";

export const INK = "#1A1F2A";
// export const EXTRA = "#e91b33"
// export const EXTRA = "#D61F3A"; // modern crimson (less neon than #e91b33)
// export const EXTRA = "#C81D3A"; // deeper / more premium
export const EXTRA = "#E11D48"; // a bit brighter but still modern
// export const EXTRA = "#C81D3A";

export const fonts = [
  "Helvetica Neue",
  "BlinkMacSystemFont",
  "sans-serif",
  "Arial",
  "Segoe UI",
];

const PRIMARY_LIGHT = {
  main: "#2E2A4F",
  light: "#3A3566", // hover/up
  dark: "#24203D",  // active/down
  contrastText: "#FFFFFF",
};

const PRIMARY_DARK = {
  // Tuned to pop on #121826 without looking neon
  main: "#4B4590",
  light: "#635BC0", // hover/up
  dark: "#3B3675", // active/down
  contrastText: "#FFFFFF",
};

const SECONDARY = {
  main: "#4F7CAC",
  light: "#6C95C2",
  dark: "#3C6290",
  contrastText: "#FFFFFF",
};


// Soft, calm shadows (works for both modes)
const shadowAmbient = "0 10px 30px";
const shadowKey = "0 2px 10px";


export const buildRandoStatTheme = (prefersDarkMode) => {
  const mode = prefersDarkMode ? "dark" : "light";

  // Backgrounds you picked (dark), and a clean light pair
  const background = prefersDarkMode
    ? { default: "#121826", paper: "#1A2236" }
    // ? { default: "#121826", paper: "#1D2740" }
    // : { default: "#F1F2F3", paper: "#FFFFFF" };
    : { default: "#F3F2EF", paper: "#FFFFFF" };

  const primary = prefersDarkMode ? PRIMARY_DARK : PRIMARY_LIGHT;

  // “Ideal” hover delta:
  // - containedPrimary: move toward primary.light (≈ +8–12% luminance)
  // - pressed/active: move toward primary.dark (≈ -8–12% luminance)
  const containedHover = primary.light;
  const containedActive = primary.dark;


  // Start with base theme so we can reuse its shadows array length (25)
  const base = createTheme({ palette: { mode } });

  const shadows = [...base.shadows];
  shadows[1] = prefersDarkMode
    ? `${shadowKey} ${alpha("#000", 0.45)}`
    : `${shadowKey} ${alpha("#111", 0.12)}`;
  shadows[2] = prefersDarkMode
    ? `${shadowKey} ${alpha("#000", 0.55)}, ${shadowAmbient} ${alpha("#000", 0.35)}`
    : `${shadowKey} ${alpha("#111", 0.16)}, ${shadowAmbient} ${alpha("#111", 0.08)}`;
  shadows[4] = prefersDarkMode
    ? `0 14px 40px ${alpha("#000", 0.45)}`
    : `0 14px 40px ${alpha("#111", 0.10)}`;

  return createTheme({
    palette: {
      mode,
      background,
      primary,
      secondary: SECONDARY,

      // Helpful for consistent “calm” UI layers
      divider: prefersDarkMode ? alpha("#FFFFFF", 0.10) : alpha(INK, 0.10),

      text: prefersDarkMode
        ? {
          primary: alpha("#FFFFFF", 0.92),
          secondary: alpha("#FFFFFF", 0.70),
          disabled: alpha("#FFFFFF", 0.45),
        }
        : {
          primary: alpha(INK, 0.92),
          secondary: alpha(INK, 0.70),
          disabled: alpha(INK, 0.40),
        },

      action: {
        // These affect default hover states for many components
        hoverOpacity: prefersDarkMode ? 0.12 : 0.08,
        selectedOpacity: prefersDarkMode ? 0.16 : 0.10,
        disabledOpacity: prefersDarkMode ? 0.38 : 0.28,
        focusOpacity: prefersDarkMode ? 0.18 : 0.12,
      },
    },

    shape: {
      borderRadius: 20,
    },

    typography: {
      fontFamily: fonts.join(","),
      button: { textTransform: "none", fontWeight: 600 },
    },

    shadows: shadows,

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: background.default,
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundImage: "none", // kills MUI's default dark-mode overlay sheen
          }),
        },
      },

      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.shape.borderRadius,
            boxShadow:
              theme.palette.mode === "light"
                ? `0 14px 32px ${alpha(INK, 0.08)}, 0 2px 8px ${alpha(INK, 0.05)}`
                : theme.shadows[1],
            transition: theme.transitions.create(
              ["transform", "box-shadow", "background-color"],
              { duration: theme.transitions.duration.shorter }
            ),
            border:
              theme.palette.mode === "light"
                ? `1px solid ${alpha(INK, 0.06)}`
                : "none",
          }),
        },
      },

      MuiButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.shape.borderRadius,
            boxShadow: "none",
            transition: theme.transitions.create(["background-color", "box-shadow", "transform"], {
              duration: theme.transitions.duration.shorter,
            }),
          }),

          containedPrimary: ({ theme }) => ({
            boxShadow: theme.shadows[1],
            "&:hover": {
              backgroundColor: containedHover,
              boxShadow: theme.shadows[2],
              transform: "translateY(-0.5px)",
            },
            "&:active": {
              backgroundColor: containedActive,
              boxShadow: theme.shadows[1],
              transform: "translateY(0px)",
            },
          }),

          outlinedPrimary: ({ theme }) => ({
            borderColor: alpha(theme.palette.primary.main, prefersDarkMode ? 0.55 : 0.35),
            "&:hover": {
              borderColor: alpha(theme.palette.primary.main, prefersDarkMode ? 0.85 : 0.55),
              backgroundColor: alpha(theme.palette.primary.main, prefersDarkMode ? 0.14 : 0.08),
            },
            "&:active": {
              backgroundColor: alpha(theme.palette.primary.main, prefersDarkMode ? 0.20 : 0.12),
            },
          }),

          textPrimary: ({ theme }) => ({
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, prefersDarkMode ? 0.14 : 0.08),
            },
            color: theme.palette.mode === "dark"
              ? theme.palette.primary.light
              : theme.palette.primary.main,
          }),
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.shape.borderRadius,
            transition: theme.transitions.create(["background-color", "transform"], {
              duration: theme.transitions.duration.shorter,
            }),
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, prefersDarkMode ? 0.14 : 0.08),
              transform: "translateY(-0.5px)",
            },
            "&:active": {
              transform: "translateY(0px)",
            },
          }),
        },
      },

      MuiChip: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.shape.borderRadius,
            backgroundColor: prefersDarkMode
              ? alpha("#FFFFFF", 0.06)
              : alpha("#000000", 0.05),
            "&:hover": {
              backgroundColor: prefersDarkMode
                ? alpha("#FFFFFF", 0.09)
                : alpha("#000000", 0.07),
            },
          }),
        },
      },

      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.shape.borderRadius,
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, prefersDarkMode ? 0.12 : 0.06),
            },
            "&.Mui-selected": {
              backgroundColor: alpha(theme.palette.primary.main, prefersDarkMode ? 0.20 : 0.10),
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, prefersDarkMode ? 0.26 : 0.14),
              },
            },
          }),
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ theme }) => ({
            borderRadius: theme.shape.borderRadius,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.mode === "light" ? alpha(theme.palette.divider, .15) : theme.palette.divider}`,
            // border: `1px solid ${theme.palette.divider}`,
          }),
        },
      },

      MuiAccordion: {
        styleOverrides: {
          root: ({ theme }) => ({
            // Prevent child content/background from visually squaring the corners.
            overflow: "hidden",
            backgroundColor: alpha(theme.palette.background.paper, 0.02),
            boxShadow: "none",
            border: theme.palette.mode === "light"
              ? `1px solid ${alpha("#000", 0.08)}`
              : `1px solid ${alpha("#fff", 0.08)}`,
          }),
          rounded: ({ theme }) => {
            const accordionRadius = theme.shape.borderRadius;
            return {
              borderRadius: 0,
              "&:first-of-type": {
                borderTopLeftRadius: accordionRadius,
                borderTopRightRadius: accordionRadius,
              },
              "&:last-of-type": {
                borderBottomLeftRadius: accordionRadius,
                borderBottomRightRadius: accordionRadius,
              },
            };
          },
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: theme.shape.borderRadius,
            boxShadow: theme.shadows[4],
            backgroundColor: theme.palette.background.paper
          }),
        },
      },

      MuiSlider: {
        styleOverrides: {
          rail: ({ theme }) => ({
            opacity: 1,
            backgroundColor:
              theme.palette.mode === "light"
                ? alpha("#1A1F2A", 0.20)
                : alpha("#fff", 0.22),
          }),
          track: ({ theme }) => ({
            backgroundColor:
              theme.palette.mode === "light"
                ? alpha("#1A1F2A", 0.70)
                : alpha("#fff", 0.70),
            border: "none",
          }),
          thumb: ({ theme }) => ({
            boxShadow: "none",
            "&:hover": {
              boxShadow:
                theme.palette.mode === "light"
                  ? `0 6px 18px ${alpha("#111", 0.18)}`
                  : `0 6px 18px ${alpha("#000", 0.45)}`,
            },
          }),
          markLabel: ({ theme }) => ({
            color: theme.palette.text.secondary,
            fontWeight: 500,
            "&.MuiSlider-markLabelActive": {
              color: theme.palette.text.primary,
              fontWeight: 700,
            },
          }),
        },
      },

    },
  });
};

/*
Usage:

const theme = useMemo(
  () => buildRandoStatTheme(prefersDarkMode, fonts),
  [prefersDarkMode, fonts]
);
*/
